import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Zap, CheckCircle, XCircle, Loader, ShieldCheck } from "lucide-react";
import { getAssistantInvite, answerAssistantInvite } from "../../api/agent";
import collingsLogo from "../../assets/collingslogo.png";

/**
 * "Will you be my Personal Assistant?" - the invitee's answer page.
 *
 * Opened from the emailed link, so it works signed out: the token in the URL is
 * the proof, and somebody reading their mail is often not logged into the CRM in
 * that browser.
 *
 * The reassurance on this page is the point of the whole flow. Adding a
 * colleague as a PA used to overwrite their account - role changed, login
 * switched off - and two agents lost access that way (Monday CRM-2026-00028).
 * Accepting now only ADDS the assistant job to the account they already have,
 * and this page says so before they click.
 */
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const AssistantInvite = () => {
  const navigate = useNavigate();
  const token = useQuery().get("token");

  const [state, setState] = useState("loading"); // loading | open | done | error
  const [invite, setInvite] = useState(null);
  const [message, setMessage] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const secondary = "#55C5D0";

  useEffect(() => {
    if (!token) {
      setState("error");
      setMessage("This invitation link is incomplete. Ask for a new one.");
      return;
    }
    getAssistantInvite(token)
      .then((res) => {
        setInvite(res.data);
        setState("open");
      })
      .catch((err) => {
        setState("error");
        setMessage(
          err?.response?.data?.detail ||
          "This invitation is no longer valid. Ask the person who invited you to send a new one."
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const answer = async (action) => {
    setSubmitting(true);
    try {
      const res = await answerAssistantInvite(token, action);
      setAccepted(action === "accept");
      setMessage(res.data?.detail || "Thanks, that is recorded.");
      setState("done");
    } catch (err) {
      setState("error");
      setMessage(
        err?.response?.data?.detail ||
        "We could not record your answer. Please try the link again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4"
      style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
    >
      <div className="bg-white rounded-3xl shadow-2xl p-12 w-full max-w-lg border border-gray-100 text-center">
        {/* Logo - matches Login.js */}
        <div className="flex flex-col items-center mb-10">
          <div className="mb-3">
            <img
              src={collingsLogo}
              alt="Collings Real Estate"
              className="h-8 sm:h-9 w-auto max-w-[180px] object-contain mx-auto"
            />
          </div>
          <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-600 font-medium">
            <Zap className="w-4 h-4 text-[#55C5D0]" />
            <span>AI-Powered Lead Generation Platform</span>
          </div>
        </div>

        {state === "loading" && (
          <div className="flex flex-col items-center gap-4">
            <Loader className="w-12 h-12 animate-spin" style={{ color: secondary }} />
            <p className="text-gray-600 text-base font-medium">Opening your invitation...</p>
          </div>
        )}

        {state === "open" && invite && (
          <div className="flex flex-col items-center gap-4">
            <ShieldCheck className="w-14 h-14" style={{ color: secondary }} />
            <h2 className="text-xl font-semibold text-gray-800">
              {invite.manager_name} has asked you to be their Personal Assistant
            </h2>
            <p className="text-gray-500 text-sm">
              You would be able to work on {invite.manager_name}'s properties
              {invite.access_level === "full_access"
                ? " and everything else they can do in the CRM."
                : ", and nothing else."}
            </p>
            <p className="text-gray-500 text-sm">
              Your own account does not change. You keep your login, your listings and
              everything you have today.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 w-full mt-4">
              <button
                onClick={() => answer("accept")}
                disabled={submitting}
                className="flex-1 py-3 px-6 rounded-xl text-white font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: secondary }}
              >
                {submitting ? "Saving..." : "Accept"}
              </button>
              <button
                onClick={() => answer("decline")}
                disabled={submitting}
                className="flex-1 py-3 px-6 rounded-xl font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Decline
              </button>
            </div>
          </div>
        )}

        {state === "done" && (
          <div className="flex flex-col items-center gap-4">
            {accepted
              ? <CheckCircle className="w-14 h-14 text-green-500" />
              : <CheckCircle className="w-14 h-14 text-gray-400" />}
            <h2 className="text-xl font-semibold text-gray-800">
              {accepted ? "You're all set" : "Invitation declined"}
            </h2>
            <p className="text-gray-500 text-sm">{message}</p>
            <button
              onClick={() => navigate("/login")}
              className="mt-4 w-full py-3 px-6 rounded-xl text-white font-medium transition-opacity hover:opacity-90"
              style={{ backgroundColor: secondary }}
            >
              Go to Login
            </button>
          </div>
        )}

        {state === "error" && (
          <div className="flex flex-col items-center gap-4">
            <XCircle className="w-14 h-14 text-red-400" />
            <h2 className="text-xl font-semibold text-gray-800">Invitation unavailable</h2>
            <p className="text-gray-500 text-sm">{message}</p>
            <button
              onClick={() => navigate("/login")}
              className="mt-4 w-full py-3 px-6 rounded-xl text-white font-medium transition-opacity hover:opacity-90"
              style={{ backgroundColor: secondary }}
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssistantInvite;
